import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, X, Upload, ImageIcon, Video as VideoIcon, Music2, Link as LinkIcon, Star, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { chunkedUpload, resolveUploadUrl } from "../../lib/upload";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalTextarea, BrutalLabel, Badge, SectionTitle,
} from "../../components/common/Brutal";

const LINK_TYPES = ["website", "facebook", "youtube", "instagram", "x", "tiktok"];

const empty = {
  name: "",
  introduction: "",
  member_type: "solo",
  members: [],
  quick_facts: [],
  links: [],
  photo_data: "",
  video_data: "",
  perform_date: "",
  perform_time: "",
  venue: "",
};

const PHOTO_MAX_MB = 5;
const VIDEO_MAX_MB = 50;

export default function AdminArtists() {
  const [artists, setArtists] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [newMember, setNewMember] = useState({ name: "", role: "" });
  const [newFact, setNewFact] = useState("");
  const [newLink, setNewLink] = useState({ type: "website", url: "" });
  const [photoUp, setPhotoUp] = useState({ active: false, pct: 0, phase: "" });
  const [videoUp, setVideoUp] = useState({ active: false, pct: 0, phase: "" });
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);
  const videoRef = useRef(null);

  const load = () => api.get("/artists").then(({ data }) => setArtists(data));

  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (a) => { setEditing(a.id); setForm({ ...empty, ...a }); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const onFile = async (e, key, maxMB, setProgress) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxMB * 1024 * 1024) {
      toast.error(`File too large (max ${maxMB}MB)`);
      e.target.value = "";
      return;
    }
    setProgress({ active: true, pct: 0, phase: "Starting…" });
    try {
      const res = await chunkedUpload(file, (pct, phase) => setProgress({ active: true, pct, phase }));
      setForm((f) => ({ ...f, [key]: res.url_path }));
      setProgress({ active: false, pct: 1, phase: "Uploaded" });
      toast.success(`${key === "photo_data" ? "Photo" : "Video"} uploaded`);
    } catch (err) {
      setProgress({ active: false, pct: 0, phase: "" });
      toast.error(err.message || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const addMember = () => {
    if (!newMember.name) return toast.error("Member name required");
    setForm((f) => ({ ...f, members: [...f.members, { ...newMember }] }));
    setNewMember({ name: "", role: "" });
  };
  const removeMember = (i) => setForm((f) => ({ ...f, members: f.members.filter((_, idx) => idx !== i) }));

  const addFact = () => {
    if (!newFact.trim()) return;
    setForm((f) => ({ ...f, quick_facts: [...f.quick_facts, newFact.trim()] }));
    setNewFact("");
  };
  const removeFact = (i) => setForm((f) => ({ ...f, quick_facts: f.quick_facts.filter((_, idx) => idx !== i) }));

  const addLink = () => {
    if (!newLink.url) return toast.error("Link URL required");
    setForm((f) => ({ ...f, links: [...f.links, { ...newLink }] }));
    setNewLink({ type: "website", url: "" });
  };
  const removeLink = (i) => setForm((f) => ({ ...f, links: f.links.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name) return toast.error("Artist name required");
    if (photoUp.active || videoUp.active) return toast.error("Wait for upload to finish");
    setSaving(true);
    try {
      if (editing === "new") await api.post("/artists", form);
      else await api.put(`/artists/${editing}`, form);
      toast.success("Saved");
      cancel();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this artist?")) return;
    await api.delete(`/artists/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-artists">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionTitle kicker="Festival Talent" title="Artists" />
        {!editing && <BrutalButton variant="primary" onClick={startNew} data-testid="artist-add"><Plus className="h-4 w-4" /> Add Artist</BrutalButton>}
      </div>

      {editing && (
        <BrutalCard className="p-5 space-y-4" data-testid="artist-form">
          <h3 className="display text-2xl">{editing === "new" ? "Add Artist" : "Edit Artist"}</h3>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Artist Name *"><BrutalInput value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="artist-name" placeholder="e.g. BINI" /></Field>
            <Field label="Member Type *">
              <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={form.member_type} onChange={(e) => setForm({...form, member_type: e.target.value})} data-testid="artist-membertype">
                <option value="solo">Solo</option>
                <option value="group">Group</option>
              </select>
            </Field>
            <Field label="Perform Date">
              <BrutalInput type="date" value={form.perform_date} onChange={(e) => setForm({...form, perform_date: e.target.value})} data-testid="artist-date" />
            </Field>
            <Field label="Perform Time">
              <BrutalInput type="time" value={form.perform_time} onChange={(e) => setForm({...form, perform_time: e.target.value})} data-testid="artist-time" />
            </Field>
            <div className="sm:col-span-2">
              <BrutalLabel>Venue</BrutalLabel>
              <BrutalInput value={form.venue} onChange={(e) => setForm({...form, venue: e.target.value})} data-testid="artist-venue" placeholder="e.g. Main Stage" />
            </div>
            <div className="sm:col-span-2">
              <BrutalLabel>Introduction</BrutalLabel>
              <BrutalTextarea value={form.introduction} onChange={(e) => setForm({...form, introduction: e.target.value})} data-testid="artist-intro" placeholder="Longer description shown on the artist page…" />
            </div>
          </div>

          {/* MEMBERS */}
          {form.member_type === "group" && (
            <div className="kf-border p-4 bg-[#FFF4E6]">
              <div className="flex items-center gap-2 mb-3"><Music2 className="h-4 w-4 text-[#E5247C]" /><div className="text-xs font-bold uppercase tracking-widest">Group Members</div></div>
              <div className="grid sm:grid-cols-3 gap-2 mb-3">
                <BrutalInput placeholder="Member name" value={newMember.name} onChange={(e) => setNewMember({...newMember, name: e.target.value})} data-testid="member-name" />
                <BrutalInput placeholder="Role (e.g. lead vocalist)" value={newMember.role} onChange={(e) => setNewMember({...newMember, role: e.target.value})} data-testid="member-role" />
                <BrutalButton variant="outline" onClick={addMember} type="button" data-testid="member-add"><Plus className="h-4 w-4" /> Add</BrutalButton>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.members.map((m, i) => (
                  <div key={i} className="kf-border bg-white px-2 py-1 text-xs flex items-center gap-2" data-testid={`member-chip-${i}`}>
                    <span><b>{m.name}</b>{m.role ? ` · ${m.role}` : ""}</span>
                    <button onClick={() => removeMember(i)} className="text-[#EF4444]"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* QUICK FACTS */}
          <div className="kf-border p-4 bg-[#FFF4E6]">
            <div className="flex items-center gap-2 mb-3"><Star className="h-4 w-4 text-[#E5247C]" /><div className="text-xs font-bold uppercase tracking-widest">Quick Facts</div></div>
            <div className="flex gap-2 mb-3">
              <BrutalInput placeholder="Add a quick fact (e.g. Grammy winner 2024)" value={newFact} onChange={(e) => setNewFact(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFact(); } }} data-testid="fact-input" />
              <BrutalButton variant="outline" onClick={addFact} type="button" data-testid="fact-add"><Plus className="h-4 w-4" /></BrutalButton>
            </div>
            <ul className="space-y-1">
              {form.quick_facts.map((f, i) => (
                <li key={i} className="kf-border bg-white px-2 py-1 text-sm flex items-center justify-between" data-testid={`fact-${i}`}>
                  <span>{f}</span>
                  <button onClick={() => removeFact(i)} className="text-[#EF4444]"><X className="h-3 w-3" /></button>
                </li>
              ))}
            </ul>
          </div>

          {/* LINKS */}
          <div className="kf-border p-4 bg-[#FFF4E6]">
            <div className="flex items-center gap-2 mb-3"><LinkIcon className="h-4 w-4 text-[#E5247C]" /><div className="text-xs font-bold uppercase tracking-widest">Links</div></div>
            <div className="grid sm:grid-cols-[160px_1fr_auto] gap-2 mb-3">
              <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={newLink.type} onChange={(e) => setNewLink({...newLink, type: e.target.value})} data-testid="link-type">
                {LINK_TYPES.map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
              </select>
              <BrutalInput placeholder="https://…" value={newLink.url} onChange={(e) => setNewLink({...newLink, url: e.target.value})} data-testid="link-url" />
              <BrutalButton variant="outline" onClick={addLink} type="button" data-testid="link-add"><Plus className="h-4 w-4" /></BrutalButton>
            </div>
            <ul className="space-y-1">
              {form.links.map((l, i) => (
                <li key={i} className="kf-border bg-white px-2 py-1 text-sm flex items-center justify-between" data-testid={`link-${i}`}>
                  <span className="truncate"><Badge color="purple" className="mr-2">{l.type}</Badge>{l.url}</span>
                  <button onClick={() => removeLink(i)} className="text-[#EF4444] shrink-0"><X className="h-3 w-3" /></button>
                </li>
              ))}
            </ul>
          </div>

          {/* MEDIA */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="kf-border p-4 bg-[#FFF4E6]">
              <div className="flex items-center gap-2 mb-3"><ImageIcon className="h-4 w-4 text-[#E5247C]" /><div className="text-xs font-bold uppercase tracking-widest">Artist Photo</div></div>
              <input ref={photoRef} type="file" accept="image/*" onChange={(e) => onFile(e, "photo_data", PHOTO_MAX_MB, setPhotoUp)} className="hidden" data-testid="artist-photo-input" />
              <div className="flex items-center gap-3">
                <BrutalButton variant="outline" type="button" onClick={() => photoRef.current?.click()} disabled={photoUp.active} data-testid="artist-photo-pick">
                  <Upload className="h-4 w-4" /> {form.photo_data ? "Replace" : "Upload"}
                </BrutalButton>
                {form.photo_data && !photoUp.active && (
                  <img src={resolveUploadUrl(form.photo_data)} alt="preview" className="h-16 w-16 object-cover kf-border" />
                )}
              </div>
              <UploadProgress state={photoUp} />
              <p className="text-[10px] text-[#6B4E9E] mt-2 uppercase tracking-widest">Max {PHOTO_MAX_MB}MB · chunked upload</p>
            </div>
            <div className="kf-border p-4 bg-[#FFF4E6]">
              <div className="flex items-center gap-2 mb-3"><VideoIcon className="h-4 w-4 text-[#E5247C]" /><div className="text-xs font-bold uppercase tracking-widest">Artist Video</div></div>
              <input ref={videoRef} type="file" accept="video/*" onChange={(e) => onFile(e, "video_data", VIDEO_MAX_MB, setVideoUp)} className="hidden" data-testid="artist-video-input" />
              <div className="flex items-center gap-3">
                <BrutalButton variant="outline" type="button" onClick={() => videoRef.current?.click()} disabled={videoUp.active} data-testid="artist-video-pick">
                  <Upload className="h-4 w-4" /> {form.video_data ? "Replace" : "Upload"}
                </BrutalButton>
                {form.video_data && !videoUp.active && <Badge color="green"><CheckCircle2 className="h-3 w-3 inline mr-1" />VIDEO READY</Badge>}
              </div>
              <UploadProgress state={videoUp} />
              <p className="text-[10px] text-[#6B4E9E] mt-2 uppercase tracking-widest">Max {VIDEO_MAX_MB}MB · chunked upload (512KB/chunk)</p>
            </div>
          </div>

          <div className="flex gap-2">
            <BrutalButton variant="primary" onClick={save} disabled={saving || photoUp.active || videoUp.active} data-testid="artist-save">
              {saving ? (<><span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>) : (<><Save className="h-4 w-4" /> Save</>)}
            </BrutalButton>
            <BrutalButton variant="outline" onClick={cancel} disabled={saving}><X className="h-4 w-4" /> Cancel</BrutalButton>
          </div>
        </BrutalCard>
      )}

      <BrutalCard className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="artists-table">
          <thead className="bg-[#FFB400]">
            <tr>
              {["Photo","Name","Type","Perform","Venue","Action"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest border-b-2 border-[#3D1E6D]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {artists.map((a, i) => (
              <tr key={a.id} className={i % 2 ? "bg-[#FFF4E6]" : "bg-white"} data-testid={`artist-row-${a.id}`}>
                <td className="px-3 py-2">{a.photo_data ? <img src={resolveUploadUrl(a.photo_data)} alt={a.name} className="h-10 w-10 object-cover kf-border" /> : <Badge color="white">—</Badge>}</td>
                <td className="px-3 py-2 font-bold">{a.name}</td>
                <td className="px-3 py-2"><Badge color={a.member_type === "group" ? "magenta" : "purple"}>{a.member_type}</Badge></td>
                <td className="px-3 py-2 text-[#6B4E9E] text-xs">{a.perform_date || "—"}{a.perform_time ? ` · ${a.perform_time}` : ""}</td>
                <td className="px-3 py-2 text-[#6B4E9E]">{a.venue}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(a)} className="kf-border bg-white p-1.5" data-testid={`artist-edit-${a.id}`}><Pencil className="h-3 w-3" /></button>
                    <button onClick={() => remove(a.id)} className="kf-border bg-[#EF4444] text-white p-1.5" data-testid={`artist-delete-${a.id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {artists.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#6B4E9E]">No artists yet. Add the first one.</td></tr>}
          </tbody>
        </table>
      </BrutalCard>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <BrutalLabel>{label}</BrutalLabel>
    {children}
  </div>
);

const UploadProgress = ({ state }) => {
  if (!state.active && state.pct === 0) return null;
  const pct = Math.round((state.pct || 0) * 100);
  const done = !state.active && state.pct >= 1;
  return (
    <div className="mt-3" data-testid="upload-progress" data-pct={pct}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-widest text-[#3D1E6D]">
          {done ? "Upload complete" : (state.phase || "Working…")}
        </span>
        <span className="text-[10px] font-bold tabular-nums text-[#E5247C]">{pct}%</span>
      </div>
      <div className="h-2 w-full kf-border bg-white overflow-hidden">
        <div
          className={`h-full transition-all duration-150 ${done ? "bg-[#22C55E]" : "bg-[#E5247C]"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};
